import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Mail, ArrowLeft } from 'lucide-react';
import Logo, { LogoMark } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';
import { getHostInfo } from '../lib/hosts';
import ThemeToggle from '../components/ThemeToggle';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import SsoButtons from '../components/SsoButtons';
import TwoFactorSetup from '../components/TwoFactorSetup';
import { MONTHLY } from '../lib/pricing';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loginWithToken } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);   // hit the email-not-verified gate
  const [resending, setResending] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Sign in with an emailed six-digit code (employees only — DESIGN-signing-in.md H/H-bis).
  // 'password' = the normal screen, 'code' = the six-digit entry screen,
  // 'twofactor'  = the second step for somebody who has 2FA switched on,
  // 'twofactorSetup' = FORCED enrolment — the grace period ran out (G8-bis).
  const [mode, setMode] = useState('password');
  const [code, setCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);

  // Two-factor challenge. `twofactorToken` proves the password was right and NOTHING else —
  // the backend refuses it as a session, so holding it here confers nothing.
  const [twofactorToken, setTwofactorToken] = useState(null);
  const [twofactorMethod, setTwofactorMethod] = useState(null);
  const [twofactorCode, setTwofactorCode] = useState('');
  const [usingRecoveryCode, setUsingRecoveryCode] = useState(false);
  // Forced enrolment (G8-bis). Same shape of promise as twofactorToken: it can enrol and
  // nothing else, and the backend refuses it as a session everywhere.
  const [setupToken, setSetupToken] = useState(null);

  // Auto-fill credentials from dev toolbar
  useEffect(() => {
    if (searchParams.get('dev_superadmin') === 'true') {
      setFormData({
        email: 'superadmin@holidayhive.co.uk',
        password: 'superadmin123'
      });
      toast.info('Superadmin credentials auto-filled');
    } else if (searchParams.get('dev_admin') === 'true') {
      setFormData({
        email: 'rob@tektraders.co.uk',
        password: 'adminadmin'
      });
      toast.info('Admin credentials auto-filled');
    }
  }, [searchParams]);

  // ⚠️ A provider sign-in that failed lands back here with a reason. Without this the
  // customer is returned to a login screen with no explanation at all, which reads as
  // "it just didn't work" — the least actionable failure there is.
  useEffect(() => {
    const problem = searchParams.get('sso_error');
    if (!problem) return;
    toast.error(problem === 'no_email'
      ? "That account didn't share an email address with us, so we can't sign you in. "
        + 'Try signing in with your email and password instead.'
      : "We couldn't complete that sign-in. Please try again, or use your email and password.");
  }, [searchParams]);

  // Ask for a code, then move to the entry screen.
  //
  // ⚠️ THE SCREEN MOVES ON WHATEVER THE ANSWER IS. The server deliberately replies with
  // the same sentence whether it sent a code, refused because the address is an admin's,
  // or has never heard of the address at all. If this branched on the response — staying
  // put for an unknown address, say — the SCREEN would leak exactly what the API was
  // careful not to, and it would be a better oracle than the login form ever was.
  const handleRequestCode = async () => {
    if (!formData.email.trim()) {
      toast.error('Enter your work email first');
      return;
    }
    setSendingCode(true);
    try {
      const response = await authApi.requestLoginCode(formData.email.trim());
      toast.success(response?.data?.message || 'Check your email for a code');
    } catch (error) {
      const httpStatus = error.response?.status;

      // A 429 is the one honest refusal: it tells them only what they just did.
      if (httpStatus === 429) {
        toast.error(error.response?.data?.detail || 'Too many requests. Try again shortly.');
        setSendingCode(false);
        return;
      }

      // ⚠️ A SERVER FAULT MUST NOT LOOK LIKE SUCCESS. This branch did not exist for the
      // first hour of this feature's life, and it cost Rob a testing round: a typo made
      // the endpoint 500 on every request, the catch below reported "check your email",
      // and the only symptom was an email that never arrived — with nothing on screen and
      // nothing in the console.
      //
      // ⚠️ It is the SAME MISTAKE as trap 7 in DESIGN-signing-in.md, where
      // `.catch(() => ({ data: [] }))` hid a 500 for a day and the page read "No customers
      // found" directly beneath "Total Customers 12".
      //
      // Showing this is safe: a 5xx says our server broke, which reveals nothing whatever
      // about the address that was typed. Only 200/4xx have to be indistinguishable.
      if (!httpStatus || httpStatus >= 500) {
        toast.error("Something went wrong at our end, so no code was sent. Please try again.");
        setSendingCode(false);
        return;
      }

      // 4xx: still not allowed to distinguish cases, so we move on exactly as if it worked.
      toast.success('Check your email for a code');
    }
    setSendingCode(false);
    setCode('');
    setMode('code');
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    const entered = code.replace(/\D/g, '');
    if (entered.length !== 6) {
      toast.error('Enter the six digits from your email');
      return;
    }
    setLoading(true);
    try {
      const response = await authApi.codeLogin(formData.email.trim(), entered);
      loginWithToken(response.data);
      toast.success('Welcome back!');
      navigate(response.data?.user?.role === 'superadmin' ? '/superadmin' : '/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      // ⚠️ email_not_verified comes back from the SHARED gate (issue_session), so a code
      // sign-in hits exactly the same wall a password sign-in does. Handle it the same way.
      if (error.response?.status === 403 && detail === 'email_not_verified') {
        setMode('password');
        setNeedsVerify(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'That code is not right, or it has expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTwofactorSubmit = async (e) => {
    e.preventDefault();
    const answer = usingRecoveryCode
      ? twofactorCode.trim()
      : twofactorCode.replace(/\D/g, '');

    if (!answer) {
      toast.error(usingRecoveryCode ? 'Enter one of your recovery codes' : 'Enter the six-digit code');
      return;
    }
    // ⚠️ Only the six-digit path is length-checked. A recovery code is a different shape
    // entirely, and validating it as six digits here would make recovery codes unusable at
    // the one moment somebody actually needs them.
    if (!usingRecoveryCode && answer.length !== 6) {
      toast.error('Enter the six digits');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.twofactorVerify(twofactorToken, answer);
      loginWithToken(response.data);
      toast.success('Welcome back!');
      navigate(response.data?.user?.role === 'superadmin' ? '/superadmin' : '/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      // The pending token lasts five minutes. Once it goes, they start again — say so
      // plainly rather than leaving them retyping codes at a screen that cannot work.
      if (error.response?.status === 401 && typeof detail === 'string' && detail.includes('expired')) {
        toast.error(detail);
        setMode('password');
        setTwofactorToken(null);
        return;
      }
      toast.error(typeof detail === 'string' ? detail : "That code isn't right.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerify(false);

    try {
      const response = await login(formData.email, formData.password);
      toast.success('Welcome back!');

      // The grace-period reminder (G8): signed in fine, but the company requires 2FA of
      // their role and the clock is running. Long duration on purpose — this toast IS the
      // "we'll remind them each time they sign in" the Security screen promises.
      if (response.twofactor_nag) {
        const days = response.twofactor_nag.days_left;
        toast.warning(
          `Your company requires two-factor authentication for your role. ` +
          `Set it up from your profile — ${days} ${days === 1 ? 'day' : 'days'} left ` +
          `before it's needed to sign in.`,
          { duration: 12000 }
        );
      }

      // Redirect superadmins to the admin panel
      if (response.user?.role === 'superadmin') {
        navigate('/superadmin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const _detail = error.response?.data?.detail;
      // First-login password change: valid credentials, but they must set
      // their own password before a session is issued.
      if (error.response?.status === 403 && _detail?.code === 'must_change_password') {
        toast.info('Please set your own password to continue');
        navigate(`/reset-password?token=${_detail.reset_token}`);
        return;
      }
      // ⚠️ NOT A FAILURE. The password was correct — this is the second half of signing in.
      // It arrives as a 403 because no session was issued, which is exactly right: a
      // session must not exist until both factors are answered.
      if (error.response?.status === 403 && _detail?.code === 'twofactor_required') {
        setTwofactorToken(_detail.twofactor_token);
        setTwofactorMethod(_detail.method);
        setTwofactorCode('');
        setUsingRecoveryCode(false);
        setMode('twofactor');
        return;
      }
      // ⚠️ ALSO NOT A FAILURE. The password was right, but their grace period ran out
      // (G8-bis): no session until they set up two-factor, and this screen is where they
      // do it. Deliberately not a lockout — enrolment IS the way through.
      if (error.response?.status === 403 && _detail?.code === 'twofactor_setup_required') {
        setSetupToken(_detail.twofactor_setup_token);
        setMode('twofactorSetup');
        return;
      }
      // Hard gate: account exists but the email hasn't been confirmed yet.
      if (error.response?.status === 403 && _detail === 'email_not_verified') {
        setNeedsVerify(true);
      } else {
        const message = error.response?.data?.detail || 'Invalid email or password';
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!formData.email) return;
    setResending(true);
    try {
      await authApi.resendVerification(formData.email);
      toast.success('Confirmation email sent — check your inbox.');
    } catch (error) {
      toast.error('Could not resend right now — please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      <ThemeToggle iconOnly variant="ghost" className="fixed top-4 right-4 z-50 text-muted-foreground hover:text-foreground" />

      
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* The logo goes HOME (the marketing site), not to "/" — on the app
              surface "/" just bounces to the dashboard, which bounces a signed-out
              visitor straight back here, so the link looked dead. Plain <a>, not
              <Link>: the homepage is a different origin (apex vs app.*). */}
          <a href={getHostInfo().marketingOrigin} className="inline-flex mb-8" data-testid="logo">
            <Logo size={32} />
          </a>

          <h1 className="font-heading text-3xl font-bold mb-2">Welcome back</h1>
          {/* ⚠️ Somebody who backed out of a provider sign-in arrives here having pressed a
              button and been moved somewhere, with nothing saying why. Closing that loop is
              the difference between "it did something" and "it ignored me" (Rob, 16/08/2026).
              Router state, not a query string — nothing here belongs in a URL or a history
              entry, and a refresh should just drop it. */}
          <p className="text-muted-foreground mb-8">
            {location.state?.ssoBackedOut
              ? "No problem — choose how you'd like to sign in."
              : "Sign in to manage your team's leave"}
          </p>

          {needsVerify && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 mb-6" data-testid="login-verify-banner">
              <p className="text-sm text-amber-700 dark:text-amber-300 font-medium mb-1">
                Please confirm your email first
              </p>
              <p className="text-sm text-muted-foreground mb-3">
                You need to click the confirmation link we emailed you before you can sign in.
              </p>
              <Button
                type="button"
                onClick={handleResend}
                variant="outline"
                size="sm"
                disabled={resending}
                data-testid="login-resend-btn"
              >
                {resending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  'Resend confirmation email'
                )}
              </Button>
            </div>
          )}

          {mode === 'twofactorSetup' ? (
            <div className="space-y-5" data-testid="twofactor-setup-forced">
              <div>
                <h2 className="text-lg font-semibold">Set up two-factor authentication</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Your password was right — but your company requires two-factor
                  authentication for your role, and the time to set it up has run out.
                  Finish setting it up below and you'll be signed straight in.
                </p>
              </div>
              <TwoFactorSetup
                setupToken={setupToken}
                onSignedIn={(session) => {
                  loginWithToken(session);
                  navigate(session?.user?.role === 'superadmin' ? '/superadmin' : '/dashboard');
                }}
                onExpired={() => { setSetupToken(null); setMode('password'); }}
              />
              <button
                type="button"
                onClick={() => { setSetupToken(null); setMode('password'); }}
                className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                data-testid="twofactor-setup-cancel"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </button>
            </div>
          ) : mode === 'twofactor' ? (
            <form onSubmit={handleTwofactorSubmit} className="space-y-5" data-testid="twofactor-form">
              <div>
                <h2 className="text-lg font-semibold">One more step</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {usingRecoveryCode
                    ? 'Enter one of the recovery codes you saved when you set this up.'
                    : twofactorMethod === 'totp'
                      ? 'Open your authenticator app and enter the six-digit code it shows.'
                      : "We've emailed you a six-digit code."}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="twofactor-code">
                  {usingRecoveryCode ? 'Recovery code' : 'Six-digit code'}
                </Label>
                <Input
                  id="twofactor-code"
                  name="one-time-code"
                  type="text"
                  /*
                    ⚠️ A recovery code contains letters and dashes, so the numeric keypad and
                    the digits-only filter must BOTH switch off for it. Getting this wrong
                    makes recovery codes untypeable on a phone — at the exact moment somebody
                    has lost their authenticator and is already stuck.
                  */
                  inputMode={usingRecoveryCode ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  maxLength={usingRecoveryCode ? 20 : 7}
                  value={twofactorCode}
                  onChange={(e) => setTwofactorCode(
                    usingRecoveryCode
                      ? e.target.value
                      : e.target.value.replace(/\D/g, '').slice(0, 6)
                  )}
                  placeholder={usingRecoveryCode ? 'k4mp-2xqr-7rtv' : '418207'}
                  autoFocus
                  className={usingRecoveryCode
                    ? 'text-center text-lg font-mono'
                    : 'text-center text-2xl tracking-[0.5em] font-mono'}
                  data-testid="twofactor-code-input"
                />
              </div>

              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
                data-testid="twofactor-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setMode('password'); setTwofactorToken(null); setTwofactorCode(''); }}
                  className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                  data-testid="twofactor-cancel"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cancel
                </button>
                {/*
                  ⚠️ THE WAY BACK IN HAS TO BE VISIBLE HERE. After decision G5 recovery codes
                  are the ONLY self-service route — an admin cannot reset another admin. If
                  this link is buried, the alternative is a support ticket only Rob can close.
                */}
                <button
                  type="button"
                  onClick={() => { setUsingRecoveryCode(!usingRecoveryCode); setTwofactorCode(''); }}
                  className="text-primary hover:underline"
                  data-testid="use-recovery-code"
                >
                  {usingRecoveryCode ? 'Use a code instead' : "Lost your phone? Use a recovery code"}
                </button>
              </div>

              {/*
                ⚠️ "THE EMAIL DIDN'T ARRIVE" MUST HAVE AN ANSWER THAT IS NOT A RECOVERY CODE.
                After decision G5 recovery codes are finite and their exhaustion ends at Rob,
                so spending one because an email was slow is a terrible trade.
              */}
              {twofactorMethod === 'email' && !usingRecoveryCode && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await authApi.twofactorResend(twofactorToken);
                      toast.success('We have sent another code');
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Couldn't send another code.");
                    }
                  }}
                  className="text-sm text-primary hover:underline"
                  data-testid="twofactor-resend"
                >
                  Didn't get it? Send another
                </button>
              )}
            </form>
          ) : mode === 'code' ? (
            <form onSubmit={handleCodeSubmit} className="space-y-5" data-testid="code-form">
              <div>
                <h2 className="text-lg font-semibold">Check your email</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  If that address can sign in with a code, we've sent one to{' '}
                  <span className="font-medium text-foreground">{formData.email}</span>.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Six-digit code</Label>
                <Input
                  id="code"
                  name="one-time-code"
                  /*
                    ⚠️ inputMode="numeric" brings up the number pad on a phone, and
                    autoComplete="one-time-code" lets iOS and Android offer the code
                    straight from the notification. The whole point of a code over a link
                    (H-bis) is that it is read on one device and typed on another, so the
                    typing has to be as close to free as we can make it.
                  */
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={7}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="418207"
                  autoFocus
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  data-testid="code-input"
                />
              </div>

              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
                data-testid="code-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setMode('password'); setCode(''); }}
                  className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                  data-testid="back-to-password"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Use a password
                </button>
                <button
                  type="button"
                  onClick={handleRequestCode}
                  disabled={sendingCode}
                  className="text-primary hover:underline disabled:opacity-60"
                  data-testid="resend-code"
                >
                  Send another
                </button>
              </div>

              <p className="text-xs text-muted-foreground">
                The code lasts 10 minutes and can only be used once.{' '}
                {/* The same countermeasure as the email body — see services/email_templates.py. */}
                Nobody from HolidayHive will ever ask you for it.
              </p>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="username"
                type="email"
                autoComplete="username"
                placeholder="you@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-hive"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-hive pr-10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex justify-end mt-1">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>

            {/*
              Sign in with an emailed code — employees only (decision H).

              ⚠️ THE RULE IS STATED ON THE PAGE, FOR EVERYONE, and that is what lets the
              server answer identically for every address. If instead we showed this only
              to employees, the screen itself would answer "is this address an admin?" —
              the account-enumeration hole Rob found on 18/08/2026, rebuilt in the UI.
            */}
            <div className="text-center pt-1">
              <button
                type="button"
                onClick={handleRequestCode}
                disabled={sendingCode}
                className="text-sm text-primary hover:underline inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="request-code-link"
              >
                {sendingCode ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                Email me a code instead
              </button>
              <p className="text-xs text-muted-foreground mt-1.5">
                Employees can sign in with a code, and managers too if your company allows it.
                Administrators need a password.
              </p>
            </div>
          </form>
          )}

          {/* Same component, same wording, same position as the sign-up screen. */}
          <SsoButtons />

          <p className="text-center text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
              Start free trial
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="hidden lg:flex flex-1 bg-card items-center justify-center relative overflow-hidden">
        <div className="hero-glow absolute inset-0" />
        <div className="hex-pattern absolute inset-0 opacity-20" />
        <div className="relative text-center max-w-md px-8">
          <LogoMark size={88} className="mx-auto mb-6" />
          <h2 className="font-heading text-3xl font-bold mb-4">
            Staff holidays, simplified.
          </h2>
          <p className="text-muted-foreground">
            Join thousands of teams managing leave with HolidayHive. 
            All features included for just {MONTHLY}/user/month.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
